import React, { useRef, useEffect, useState, useContext, forwardRef, useImperativeHandle } from 'react';
import * as Tone from 'tone';
import '../styles/sound-making.css';
import { SpectrogramContext } from './spectrogram-provider';

import { generateScale, generateFullScale } from '../util/generateScale';
import {
  getFreq,
  getGain,
  //getTempo,
  freqToIndex,
  getMousePos,
  convertToLog,
  midiToFreq,
  gainToLinear,
  //getHarmonicWeightInExpScale,
} from '../util/conversions';
// import WebMidi from 'webmidi';
const NUM_VOICES = 6;
const RAMPVALUE = 0.2;
const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
let harmonicWeights = new Array(99);
for (let i = 0; i < 99; i++) {
  harmonicWeights[i] = 1;
}

const soundMakingfftSize = 2048 * 2;
const NOTE_LINE_WIDTH = 250;    // width of section on the canvas where notelines do not reach
const MAX_SYNTH_DURATION = 2;

// Main sound-making class. Can handle click and touch inputs
const SoundMaking = forwardRef((props, ref) => {
  const analyser = useRef(props.analyser);
  if (analyser != null) { analyser.current.fftSize = soundMakingfftSize; }

  // use refs, not state. Keep track of these without requiring a rerender
  const mouseDown = useRef(false);
  const touch = useRef(false);

  const [currentVoice, setCurrentVoice] = useState(0);
  const [feedback, setFeedback] = useState(false);
  const [amOn, setAmOn] = useState(false);
  const [fmOn, setFmOn] = useState(false);
  const [pitchButtonPressed, setPitchButtonPressed] = useState(false);
  const [excludeScaleState, setExcludeScaleState] = useState(false);
  // this.onMouseDown = this.onMouseDown.bind(this);
  // this.onMouseUp = this.onMouseUp.bind(this);
  // this.onMouseMove = this.onMouseMove.bind(this);
  // this.onMouseOut = this.onMouseOut.bind(this);
  // this.onTouchStart = this.onTouchStart.bind(this);
  // this.onTouchEnd = this.onTouchEnd.bind(this);
  // this.onTouchMove = this.onTouchMove.bind(this);

  // SET CONTEXT
  const spectroContext = useContext(SpectrogramContext);
  const canvasRef = useRef(null);

  // Set Refs for this.synths, this.amSignals, this.masterVolume, etc.
  const synths = useRef(new Array(NUM_VOICES));
  const amSignals = useRef(new Array(NUM_VOICES));
  const fmSignals = useRef(new Array(NUM_VOICES));
  const heldFreqs = useRef(new Array(NUM_VOICES));
  const heldIds = useRef(new Array(NUM_VOICES));
  const bendStartPercents = useRef(new Array(NUM_VOICES));
  const bendStartFreqs = useRef(new Array(NUM_VOICES));
  const bendStartVolumes = useRef(new Array(NUM_VOICES));
  const midiNotes = useRef(new Array(NUM_VOICES));

  const masterVolume = useRef(new Tone.Volume(0));
  const ctx = useRef(null);

  const limiter = useRef(null);

  const reverbVolume = useRef(new Tone.Volume(0));
  const reverb = useRef(null);
  const delayVolume = useRef(new Tone.Volume(0))
  const delay = useRef(null);

  const frequencies = useRef({});

  const scaleLabel = useRef("");

  // Setup Tone and all of its needed dependencies.
  // To view signal flow, check out signal_flow.png
  useEffect( () => {
    Tone.start();
    Tone.setContext(props.audioContext);
    Tone.getContext().lookAhead = 0;

    analyser.current = props.analyser;
    if (analyser.current != null) { analyser.current.fftSize = soundMakingfftSize; }

    // Array to hold synthesizer objects. Implemented in a circular way
    // so that each new voice (touch input) is allocated, it is appended to the
    // array until the array is full and it then appends the next voice to array[0]
    synths.current = new Array(NUM_VOICES);
    amSignals.current = new Array(NUM_VOICES);
    fmSignals.current = new Array(NUM_VOICES);
    heldFreqs.current = new Array(NUM_VOICES);
    heldIds.current = new Array(NUM_VOICES);
    bendStartPercents.current = new Array(NUM_VOICES);
    bendStartFreqs.current = new Array(NUM_VOICES);
    bendStartVolumes.current = new Array(NUM_VOICES);
    midiNotes.current = new Array(NUM_VOICES);

    // Start master volume at -20 dB
    masterVolume.current = new Tone.Volume(0);
    ctx.current = canvasRef.current.getContext('2d');
    let options = {
      oscillator: {
        type: 'custom',
        partials: [1],
      },
      envelope: {
        attack: 0.1,
      },
    };
    let options2 = {
      oscillator: {
        type: 'sine',
      },
    };

    // For each voice, create a synth and connect it to the master volume
    for (let i = 0; i < NUM_VOICES; i++) {
      synths.current[i] = new Tone.Synth(options);
      synths.current[i].connect(masterVolume.current);
      synths.current[i].sync();
      amSignals.current[i] = new Tone.Synth(options2);
      amSignals.current[i].connect(synths.current[i].volume);
      fmSignals.current[i] = new Tone.Synth(options2);
      fmSignals.current[i].connect(synths.current[i].frequency);
      bendStartPercents.current[i] = 0;
      bendStartFreqs.current[i] = 0;
      bendStartVolumes.current[i] = 0;
    }
    // this.drawPitchBendButton(false);
    // Limiter at -5db
    limiter.current = new Tone.Limiter(-5).toDestination();
    // limiter.current.connect(Tone.getDestination());
    masterVolume.current.connect(limiter.current); // Master volume receives all of the synthesizer inputs and sends them to the speakers

    if (iOS) {
      reverbVolume.current = new Tone.Volume(-10);
      reverb.current = new Tone.JCReverb(spectroContext.state.reverbDecay * 0.9);
      let m = new Tone.Mono(); // JCReverb is a stereo algorithmic reverb. This makes is mono again.
      reverb.current.connect(m);
      m.connect(reverbVolume.current);
      // reverb.current.connect(reverbVolume.current);
    } else {
      reverbVolume.current = new Tone.Volume(0);
      reverb.current = new Tone.Reverb(spectroContext.state.reverbDecay * 20 + 0.1); // Reverb unit. Runs in parallel to masterVolume
      reverb.current.generate().then(() => {
        reverb.current.connect(reverbVolume.current);
      });
    }
    reverbVolume.current.mute = true;
    reverbVolume.current.connect(limiter.current);
    masterVolume.current.connect(reverb.current);
    delay.current = new Tone.FeedbackDelay(
      spectroContext.state.delayTime + 0.01,
      spectroContext.state.delayFeedback
    ); // delay unit. Runs in parallel to masterVolume
    masterVolume.current.connect(delay.current);

    // this.amSignal.volume.value = -Infinity;

    delayVolume.current = new Tone.Volume(0);
    delayVolume.current.mute = true;

    delay.current.connect(delayVolume.current);

    delayVolume.current.connect(limiter.current);
    // Sound Off by default
    masterVolume.current.mute = !spectroContext.state.soundOn;
    // Object to hold all of the note-line frequencies (for checking the gold lines)
    frequencies.current = {};
    if (spectroContext.state.noteLinesOn) {
      renderNoteLines();
    }
    window.addEventListener('resize', handleResize);
    Tone.getTransport().bpm.value = 200;
    Tone.getTransport().start();

    // componentWillUmount
    return() => {
      masterVolume.current.mute = true;
      window.removeEventListener('resize', handleResize);
    }

  }, [] );

  // Sets up what will happen on controls changes
  const setAudioVariables = () => {
    masterVolume.current.connect(limiter.current); // Master volume receives all of the synthesizer inputs and sends them to the speakers
    if (spectroContext.state.soundOn === false) {
      masterVolume.current.mute = true;
    } else {
      masterVolume.current.mute = false;
    }
    if (
      masterVolume.current.mute === false &&
      spectroContext.state.outputVolume &&
      spectroContext.state.outputVolume !== masterVolume.current.volume.value
    ) {
      masterVolume.current.volume.value = getGain(1 - spectroContext.state.outputVolume / 100);
    }
    // if (spectroContext.state.timbre !== synths.current[0].oscillator.type) {
    //   let newTimbre = spectroContext.state.timbre.toLowerCase();
    //   for (let i = 0; i < NUM_VOICES; i++) {
    //     synths.current[i].oscillator.type = newTimbre;
    //   }
    // }
    // for(let i = 0; i< NUM_VOICES; i++){
    //   synths.current[i].envelope.attack = 0.2;

    // }

    if (spectroContext.state.harmonicsOn) {
      for (let i = 0; i < NUM_VOICES; i++) {
        synths.current[i].oscillator.type = 'custom';
        synths.current[i].oscillator.partials = harmonicWeights.slice(
          0,
          spectroContext.state.numHarmonics + 1
        );
      }
    }
    // if (spectroContext.state.attack !== synths.current[0].envelope.attack) {
    //   for (let i = 0; i < NUM_VOICES; i++) {
    //     synths.current[i].envelope.attack = spectroContext.state.attack;
    //   }
    // }
    // if (spectroContext.state.release !== synths.current[0].envelope.release) {
    //   for (let i = 0; i < NUM_VOICES; i++) {
    //     synths.current[i].envelope.release = spectroContext.state.release;
    //   }
    // }
    if (spectroContext.state.headphoneMode) {
      // If Headphone Mode, connect the masterVolume to the graph
      if (!feedback) {
        limiter.current.connect(analyser.current);
        setFeedback(true);
      }
    } else {
      if (feedback) {
        limiter.current.disconnect(analyser.current);
        setFeedback(false);
      }
    }
    if (spectroContext.state.delayOn) {
      if (delayVolume.current.mute) {
        delayVolume.current.mute = false;
      }
      masterVolume.current.disconnect(delay.current);
      delay.current = null;
      delay.current = new Tone.FeedbackDelay(
        spectroContext.state.delayTime + 0.01,
        spectroContext.state.delayFeedback * 0.95
      );
      delay.current.connect(delayVolume.current);
      masterVolume.current.connect(delay.current);
    } else if (!spectroContext.state.delayOn && !delayVolume.current.mute) {
      delayVolume.current.mute = true;
    }
    if (spectroContext.state.reverbOn) {
      if (reverbVolume.current.mute) {
        reverbVolume.current.mute = false;
      }
      masterVolume.current.disconnect(reverb.current);
      reverb.current = null;
      if (iOS) {
        reverb.current = new Tone.JCReverb(spectroContext.state.reverbDecay * 0.9);
        let m = new Tone.Mono();
        reverb.current.connect(m);
        m.connect(reverbVolume.current);
        // console.log(reverb.current.numberOfInputs, reverb.current.numberOfOutputs)
        // reverb.current.connect(reverbVolume.current);
      } else {
        reverb.current = new Tone.Reverb(spectroContext.state.reverbDecay * 20 + 0.1); // Reverb unit. Runs in parallel to masterVolume
        reverb.current.generate().then(() => {
          reverb.current.connect(reverbVolume.current);
          // reverb.current.decay = spectroContext.state.reverbDecay*15;
        });
      }
      masterVolume.current.connect(reverb.current);
    } else if (!spectroContext.state.reverbOn && !reverbVolume.current.mute) {
      reverbVolume.current.mute = true;
    }
    if (iOS) {
      limiter.current.connect(analyser.current);
      masterVolume.current.connect(reverb.current);
      masterVolume.current.connect(delay.current);
      masterVolume.current.connect(limiter.current);
    }
  }

  const checkHeldFreq = (voice) => {
    // console.log("Checking")
    // console.log(synths.current[voice])
    synths.current[voice].triggerRelease();
    // let osc_outer = synths.current[voice].oscillator._state._timeline
    // let osc_inner = synths.current[voice].oscillator._oscillator._state._timeline
    // console.log(osc_outer)
    // console.log(osc_inner)
    // if (osc_outer.slice(-1).state != osc_inner.slice(-1).state) {
    //   console.log("NOT MATCHING STATE")
    //   console.log(osc_outer.slice(-1))
    //   console.log(osc_inner.slice(-1))
    //   synths.current[voice].triggerRelease();
    // }

    // for (let i = 0; i < synths.current.length; i++) {
    //   synths.current[i].triggerRelease();
    //   console.log(synths.current[voice].oscillator._state._timeline)
    //   if (i == 0)
    //     console.log(synths.current[0])
    // }
    
    // let int = window.setInterval(()=>{
    //   if(synths.current[voice].oscillator.state === "stopped"){
    //     window.clearInterval(int);
    //   } else {
    //     synths.current[voice].triggerRelease();
    //   }
    // }, 1000);
  }

  /**
  This Section controls how the SoundMaking(s) react to user input
  */
  const onMouseDown = (e) => {
    e.preventDefault(); // Always need to prevent default browser choices
    if (!spectroContext.state.midi && spectroContext.state.soundOn) {
      setAudioVariables();
      let pos = getMousePos(canvasRef.current, e);
      // Calculates x and y value in respect to width and height of screen
      // The value goes from 0 to 1. (0, 0) = Bottom Left corner
      let yPercent = 1 - pos.y / spectroContext.state.height;
      let xPercent = 1 - pos.x / spectroContext.state.width;

      mouseDown.current = true;

      let mouseClickType = e.nativeEvent.buttons;
      let excludeScale = true;

      // check if within non-scale right side section
      if (spectroContext.state.width - pos.x <= NOTE_LINE_WIDTH || mouseClickType === 2) {
        excludeScale = true;
      }
      else {
        excludeScale = false;
      }

      let freq = sm_getFreq(yPercent, excludeScale,);
      let gain = getGain(xPercent);
      // newVoice = implementation of circular array discussed above.
      // let newVoice = (this.state.currentVoice + 1) % NUM_VOICES; // Mouse always changes to new "voice"
      let newVoice = 0;
      
      // QUANTIZE (NOT BEING USED CURRENTLY)
      if (spectroContext.state.quantize) {
        Tone.getTransport().scheduleRepeat((time) => {
          synths.current[newVoice].triggerAttackRelease(heldFreqs.current[newVoice], '@8n.'); // Starts the synth at frequency = freq
        }, '4n');
        heldFreqs.current[newVoice] = freq;
        synths.current[newVoice].volume.value = gain; // Starts the synth at volume = gain
        // console.log(getTempo(xPercent), Tone.Transport.position, Tone.Transport.bpm)
        // Tone.Transport.bpm.value = getTempo(xPercent);
      }
      
      else {
        let max = -Infinity;
        if (spectroContext.state.harmonicsOn) {
          synths.current[newVoice].oscillator.partials = harmonicWeights
          .slice(0, spectroContext.state.numHarmonics + 1)
          .map((weight, index) => {
            let weightFromFilter = getFilterCoeficients(freq * (index + 1));
            if (weightFromFilter > max) {
              max = weightFromFilter;
            }
            return weightFromFilter * weight;
          });
        }
        else {
          synths.current[newVoice].oscillator.partials = harmonicWeights
            .slice(0, 1)
            .map((weight, index) => {
              let weightFromFilter = getFilterCoeficients(freq * (index + 1));
              if (weightFromFilter > max) {
                max = weightFromFilter;
              }
              return weightFromFilter * weight;
            });
        }
        
        let newGain = gain + 20 * Math.log10(max);
        // synths.current[newVoice].triggerAttackRelease(freq, MAX_SYNTH_DURATION); // Starts the synth at frequency = freq
        synths.current[newVoice].triggerAttack(freq, Tone.now()); // Starts the synth at frequency = freq
        synths.current[newVoice].volume.value = newGain; // Starts the synth at volume = gain
        // console.log(gain, 20*Math.log10(max), gain+20*Math.log10(max))
      }

      // Am
      if (spectroContext.state.amOn) {
        let newVol = convertToLog(spectroContext.state.amLevel, 0, 1, 0.01, 15); // AM amplitud;e set between 0.01 and 15 (arbitray choices)
        let newFreq = convertToLog(spectroContext.state.amRate, 0, 1, 0.5, 50); // AM frequency set between 0.5 and 50 hz (arbitray choices)
        amSignals.current[newVoice].volume.exponentialRampToValueAtTime(
          newVol,
          props.audioContext.currentTime + 1
        ); // Ramps to AM amplitude in 1 sec
        amSignals.current[newVoice].triggerAttack(newFreq, Tone.now());
      }
      // FM
      if (spectroContext.state.fmOn) {
        let modIndex = (1 - freqToIndex(freq, 20000, 20, 1)) * 1.2; // FM index ranges from 0 - 2
        let newVol = convertToLog(spectroContext.state.fmLevel, 0, 1, 40, 100); // FM amplitude set between 30 and 60 (arbitrary choices)
        let newFreq = convertToLog(spectroContext.state.fmRate, 0, 1, 0.5, 50); // FM Frequency set between 0.5 and 50 (arbitray choices)
        // let newFreq = convertToLog(yPercent, 0, 1, 0.5, 20); // FM Frequency set between 0.5 and 20 (arbitray choices)
        fmSignals.current[newVoice].volume.exponentialRampToValueAtTime(
          newVol * modIndex,
          props.audioContext.currentTime + RAMPVALUE
        ); // Ramps to FM amplitude*modIndex in RAMPVALUE sec
        fmSignals.current[newVoice].triggerAttack(newFreq, Tone.now());
      }

      ctx.current.clearRect(0, 0, spectroContext.state.width, spectroContext.state.height); // Clears canvas for redraw of label
      if (spectroContext.state.noteLinesOn) {
        renderNoteLines();
      }
      setCurrentVoice(newVoice);
      setExcludeScaleState(excludeScale);

      // This should happen after setting state. Used to be within a setState callback
      //this.label(freq, pos.x, pos.y); // Labels the point
      drawHarmonics(newVoice, freq, pos.x, excludeScale);
      // this.drawPitchBendButton(false);

    }
  };

  const onMouseMove = (e) => {
    e.preventDefault(); // Always need to prevent default browser choices
    if (mouseDown.current) {
      // Only want to change when mouse is pressed
      // The next few lines are similar to onMouseDown
      let index = 0;
      let { height, width } = spectroContext.state;
      let pos = getMousePos(canvasRef.current, e);
      let yPercent = 1 - pos.y / height;
      let xPercent = 1 - pos.x / width;

      let mouseClickType = e.nativeEvent.buttons;
      let excludeScale;

      // check if within non-scale right side section
      if (width - pos.x <= NOTE_LINE_WIDTH || mouseClickType === 2) {
        excludeScale = true;
      }
      else {
        excludeScale = false;
      }
      let gain = getGain(xPercent);
      let freq = sm_getFreq(yPercent, excludeScale,);

      // FM
      if (spectroContext.state.fmOn) {
        let modIndex = (1 - freqToIndex(freq, 20000, 20, 1)) * 1.2;
        let newVol = convertToLog(spectroContext.state.fmLevel, 0, 1, 40, 100); // FM amplitude set between 30 and 60 (arbitrary choices)
        let newFreq = convertToLog(spectroContext.state.fmRate, 0, 1, 0.5, 50); // FM Frequency set between 0.5 and 50 (arbitray choices)
        // let newFreq = convertToLog(yPercent, 0, 1, 0.5, 20); // FM Frequency set between 0.5 and 20 (arbitray choices)
        fmSignals.current[index].volume.exponentialRampToValueAtTime(
          newVol * modIndex,
          props.audioContext.currentTime + RAMPVALUE
        ); // Ramps to FM amplitude*modIndex in RAMPVALUE sec
        fmSignals.current[index].triggerAttack(newFreq, Tone.now()+0.001);  //+0.001 added to fix somewhat rare case where mouseMove occurs at time of mousedown, it is not the same exact time, which would have produced an irrecoverable error
      }

      // Clears the label
      ctx.current.clearRect(0, 0, width, height);
      if (spectroContext.state.noteLinesOn) {
        renderNoteLines();
      }
      // this.drawPitchBendButton(false);
      // this.label(freq, pos.x, pos.y);
      let max = -Infinity;
      if (spectroContext.state.harmonicsOn) {
        synths.current[index].oscillator.partials = harmonicWeights
          .slice(0, spectroContext.state.numHarmonics + 1)
          .map((weight, index) => {
            let weightFromFilter = getFilterCoeficients(freq * (index + 1));
            if (weightFromFilter > max) {
              max = weightFromFilter;
            }
            return weightFromFilter * weight;
          });
      }
      else {
        synths.current[index].oscillator.partials = harmonicWeights
          .slice(0, 1)
          .map((weight, index) => {
            let weightFromFilter = getFilterCoeficients(freq * (index + 1));
            if (weightFromFilter > max) {
              max = weightFromFilter;
            }
            return weightFromFilter * weight;
          });
      }
      let newGain = gain + 20 * Math.log10(max);
      if (spectroContext.state.scaleOn && ((width - pos.x) > NOTE_LINE_WIDTH) && mouseClickType !== 2) {
        // Jumps to new Frequency and Volume
        if (spectroContext.state.quantize) {
          heldFreqs.current[index] = freq;
        } else {
          // get previous frequency
          let prev_freq = synths.current[index].frequency.value

          if (prev_freq != freq) {
            synths.current[index].frequency.value = freq;
          }
          synths.current[index].volume.value = newGain;
        }
      } else {
        if (spectroContext.state.quantize) {
          heldFreqs.current[index] = freq;
        } else {
          // Ramps to new Frequency and Volume
          synths.current[index].frequency.exponentialRampToValueAtTime(
            freq,
            props.audioContext.currentTime + RAMPVALUE
          );
          // // Ramp to new Volume
          synths.current[index].volume.exponentialRampToValueAtTime(
            newGain,
            props.audioContext.currentTime + RAMPVALUE
          );
        }
      }
      setExcludeScaleState(excludeScale);

      // this used to be in a callback function after setting state

      // callback to draw harmonics, so that excludeScale state has been set before calling drawHarmonics and labeling
      //this.label(freq, pos.x, pos.y); // Labels the point
      drawHarmonics(index, freq, pos.x, excludeScale);
      // this.drawPitchBendButton(false);

    }
    // INTERESTINGLY, IF THIS IS ACTIVE, THEN THE TONE KEEPS PLAYING IF THE MOUSE IS MOVED AROUND. THERE MAY BE SOMETHING GOING ON HERE
    // else {
    //   Tone.getTransport().cancel();
    //   // console.log("the synths start coming and they don't stop coming...")
    // }
  };

  const onMouseUp = (e) => {
    e.preventDefault(); // Always need to prevent default browser choices
    // Only need to trigger release if synth exists (a.k.a mouse is down)
    if (mouseDown.current && !spectroContext.state.sustain) {
      let index = 0;
      let freq_to_stop = synths.current[index].frequency.value;
      // Tone.getTransport().cancel();
      Tone.getTransport().cancel();
      synths.current[index].triggerRelease(); // Relase frequency, volume goes to -Infinity
      amSignals.current[index].triggerRelease();
      fmSignals.current[index].triggerRelease();
      synths.current[index].triggerAttackRelease(freq_to_stop, 0.001);
      // synths.current[index].triggerAttackRelease();
      checkHeldFreq(index);
      // Clears the label
      ctx.current.clearRect(0, 0, spectroContext.state.width, spectroContext.state.height);
      if (spectroContext.state.noteLinesOn) {
        renderNoteLines();
      }
      // this.drawPitchBendButton(false);
    }
    mouseDown.current = false;
  };

  /* This is a similar method to onMouseUp. Occurs when mouse exists canvas */
  const onMouseOut = (e) => {
    onMouseUp(e);
  };

  /*The touch section is the same as the mouse section with the added feature of
  multitouch and vibrato. For each finger down, this function places a frequency
  and volume value into the next available position in the synth array
  (implmented as a circular array).
  */
  const onTouchStart = (e) => {
    e.preventDefault(); // Always need to prevent default browser choices
    e.stopPropagation();
    if (!spectroContext.state.midi && spectroContext.state.soundOn) {
      setAudioVariables();
      drawPitchBendButton(false, e.touches.length);

      if (e.touches.length > NUM_VOICES) {
        return;
      }
      ctx.current.clearRect(0, 0, spectroContext.state.width, spectroContext.state.height);

      if (spectroContext.state.noteLinesOn) {
        renderNoteLines();
      }
      // For each finger, do the same as above in onMouseDown
      for (let i = 0; i < e.changedTouches.length; i++) {
        let pos = getMousePos(canvasRef.current, e.changedTouches[i]);
        if (!isPitchButton(pos.x, pos.y)) {
          let yPercent = 1 - pos.y / spectroContext.state.height;
          let xPercent = 1 - pos.x / spectroContext.state.width;

          let excludeScale;
          // check if within non-scale right side section
          if (spectroContext.state.width - pos.x <= NOTE_LINE_WIDTH) {
            excludeScale = true;
          }
          else {
            excludeScale = false;
          }
          setExcludeScaleState(excludeScale);

          let gain = getGain(xPercent);
          let freq = sm_getFreq(yPercent, excludeScale);
          let newVoice = e.changedTouches[i].identifier % NUM_VOICES;
          if (newVoice < 0) newVoice = NUM_VOICES + newVoice;
          
          touch.current = true;

          if (spectroContext.state.quantize) {
            let id = Tone.getTransport().scheduleRepeat((time) => {
              synths.current[newVoice].triggerAttackRelease(heldFreqs.current[newVoice], '@8n.'); // Starts the synth at frequency = freq
            }, '4n');
            heldFreqs.current[newVoice] = freq;
            heldIds.current[newVoice] = id;
          } else {
            synths.current[newVoice].triggerAttack(freq, Tone.now());
          }
          // Am
          if (spectroContext.state.amOn) {
            let newVol = convertToLog(spectroContext.state.amLevel, 0, 1, 0.01, 15); // AM amplitud;e set between 0.01 and 15 (arbitray choices)
            let newFreq = convertToLog(spectroContext.state.amRate, 0, 1, 0.5, 50); // AM frequency set between 0.5 and 50 hz (arbitray choices)
            amSignals.current[newVoice].volume.exponentialRampToValueAtTime(
              newVol,
              props.audioContext.currentTime + 1
            ); // Ramps to AM amplitude in 1 sec
            amSignals.current[newVoice].triggerAttack(newFreq, Tone.now());
          }
          // FM
          if (spectroContext.state.fmOn) {
            let modIndex = (1 - freqToIndex(freq, 20000, 20, 1)) * 1.2; // FM index ranges from 0 - 2
            let newVol = convertToLog(spectroContext.state.fmLevel, 0, 1, 40, 100); // FM amplitude set between 30 and 60 (arbitrary choices)
            let newFreq = convertToLog(spectroContext.state.fmRate, 0, 1, 0.5, 50); // FM Frequency set between 0.5 and 50 (arbitray choices)
            // let newFreq = convertToLog(yPercent, 0, 1, 0.5, 20); // FM Frequency set between 0.5 and 20 (arbitray choices)
            fmSignals.current[newVoice].volume.exponentialRampToValueAtTime(
              newVol * modIndex,
              props.audioContext.currentTime + RAMPVALUE
            ); // Ramps to FM amplitude*modIndex in RAMPVALUE sec
            fmSignals.current[newVoice].triggerAttack(newFreq, Tone.now());
          }

          drawPitchBendButton(pitchButtonPressed, e.touches.length);

          let max = 0;
          if (spectroContext.state.harmonicsOn) {
            synths.current[newVoice].oscillator.partials = harmonicWeights
              .slice(0, spectroContext.state.numHarmonics + 1)
              .map((weight, index) => {
                let weightFromFilter = getFilterCoeficients(freq * (index + 1));
                if (weightFromFilter > max) {
                  max = weightFromFilter;
                }
                return weightFromFilter * weight;
              });
          }
          else {
            synths.current[newVoice].oscillator.partials = harmonicWeights
              .slice(0, 1)
              .map((weight, index) => {
                let weightFromFilter = getFilterCoeficients(freq * (index + 1));
                if (weightFromFilter > max) {
                  max = weightFromFilter;
                }
                return weightFromFilter * weight;
              });
          }
          let newGain = gain + 20 * Math.log10(max);
          synths.current[newVoice].volume.value = newGain;
          if (pitchButtonPressed) {
            bendStartPercents.current[newVoice] = yPercent;
            bendStartFreqs.current[newVoice] = freq;
            bendStartVolumes.current[newVoice] = newGain;
          }
        } else {
          // If Touching Pitch Button, calculate the starting values of the pitchbend
          for (let i = 0; i < e.touches.length; i++) {
            let pos = getMousePos(canvasRef.current, e.touches[i]);
            let index = e.touches[i].identifier % NUM_VOICES;
            if (index < 0) index = NUM_VOICES + index;
            let yPercent = 1 - pos.y / spectroContext.state.height;
            let xPercent = 1 - pos.x / spectroContext.state.width;
            let gain = getGain(xPercent);

            let excludeScale;
            // check if within non-scale right side section
            if (spectroContext.state.width - pos.x <= NOTE_LINE_WIDTH) {
              excludeScale = true;
            }
            else {
              excludeScale = false;
            }
            
            let freq = sm_getFreq(yPercent,excludeScale);
            if (!isPitchButton(pos.x, pos.y)) {
              bendStartPercents.current[index] = yPercent;
              bendStartFreqs.current[index] = freq;
              bendStartVolumes.current[index] = gain;
            }
          }
          drawPitchBendButton(true, e.touches.length);
          setPitchButtonPressed(true);
        }
      }
      for (let i = 0; i < e.touches.length; i++) {
        let pos = getMousePos(canvasRef.current, e.touches[i]);
        let yPercent = 1 - pos.y / spectroContext.state.height;
        // let xPercent = 1 - pos.x / spectroContext.state.width;

        let excludeScale;
        // check if within non-scale right side section. (Could simplify this by putting it directly within getFreq param but its ok)
        if (spectroContext.state.width - pos.x <= NOTE_LINE_WIDTH) {
          excludeScale = true;
        }
        else {
          excludeScale = false;
        }

        let freq = sm_getFreq(yPercent,excludeScale);
        if (!isPitchButton(pos.x, pos.y)) {
          // this.label(freq, pos.x, pos.y);
          let index = e.touches[i].identifier % NUM_VOICES;
          if (index < 0) index = NUM_VOICES + index;
          drawHarmonics(index, freq, pos.x, excludeScale);
        }
      }
    }
  };

  const onTouchMove = (e) => {
    e.preventDefault(); // Always need to prevent default browser choices
    // Check if more fingers were moved than allowed
    if (touch.current) {
      if (e.changedTouches.length > NUM_VOICES) {
        return;
      }
      let { width, height } = spectroContext.state;
      // If touch is pressed (Similar to mouseDown = true, although there should never be a case where this is false)
      ctx.current.clearRect(0, 0, width, height);
      if (spectroContext.state.noteLinesOn) {
        renderNoteLines();
      }
      // Determines the current "starting" index to change
      // For each changed touch, do the same as onMouseMove
      for (let i = 0; i < e.changedTouches.length; i++) {
        let pos = getMousePos(canvasRef.current, e.changedTouches[i]);
        let yPercent = 1 - pos.y / spectroContext.state.height;
        let xPercent = 1 - pos.x / spectroContext.state.width;
        
        let excludeScale;
        // check if within non-scale right side section
        if (width - pos.x <= NOTE_LINE_WIDTH) {
          excludeScale = true;
        }
        else {
          excludeScale = false;
        }
        setExcludeScaleState(excludeScale);

        let gain = getGain(xPercent);

        let freq = sm_getFreq(yPercent,excludeScale,);
        // Determines index of the synth needing to change volume/frequency
        let index = e.changedTouches[i].identifier % NUM_VOICES;
        if (index < 0) index = NUM_VOICES + index;
        // Deals with rounding issues with the note lines
        let oldFreq = synths.current[index].frequency.value;
        for (let note in frequencies.current) {
          if (Math.abs(frequencies.current[note] - oldFreq) < 0.1 * oldFreq) {
            oldFreq = frequencies.current[note];
          }
        }
        // FM
        if (spectroContext.state.fmOn) {
          let modIndex = (1 - freqToIndex(freq, 20000, 20, 1)) * 1.2; // FM index ranges from 0 - 2
          let newVol = convertToLog(spectroContext.state.fmLevel, 0, 1, 40, 100); // FM amplitude set between 30 and 60 (arbitrary choices)
          let newFreq = convertToLog(spectroContext.state.fmRate, 0, 1, 0.5, 50); // FM Frequency set between 0.5 and 50 (arbitray choices)
          // let newFreq = convertToLog(yPercent, 0, 1, 0.5, 20); // FM Frequency set between 0.5 and 20 (arbitray choices)
          fmSignals.current[index].volume.exponentialRampToValueAtTime(
            newVol * modIndex,
            props.audioContext.currentTime + RAMPVALUE
          ); // Ramps to FM amplitude*modIndex in RAMPVALUE sec
          fmSignals.current[index].triggerAttack(newFreq);
        }
        let max = -Infinity;
        if (spectroContext.state.harmonicsOn) {
          synths.current[index].oscillator.partials = harmonicWeights
            .slice(0, spectroContext.state.numHarmonics + 1)
            .map((weight, index) => {
              let weightFromFilter = getFilterCoeficients(freq * (index + 1));
              if (weightFromFilter > max) {
                max = weightFromFilter;
              }
              return weightFromFilter * weight;
            });
        } else {
          synths.current[index].oscillator.partials = harmonicWeights
            .slice(0, 1)
            .map((weight, index) => {
              let weightFromFilter = getFilterCoeficients(freq * (index + 1));
              if (weightFromFilter > max) {
                max = weightFromFilter;
              }
              return weightFromFilter * weight;
            });
        }
        // console.log("Volume: " + getGain(xPercent));
        // console.log("partials: " + synths.current[index].oscillator.partials);
        let newGain = gain + 20 * Math.log10(max);
        // These are the same as onMouseMove
        if (spectroContext.state.scaleOn && ((width - pos.x) > NOTE_LINE_WIDTH) && !pitchButtonPressed) {
          // Jumps to new Frequency and Volume
          if (spectroContext.state.quantize) {
            heldFreqs.current[index] = freq;
          } else {
            synths.current[index].frequency.value = freq;
          }
          synths.current[index].volume.value = newGain;
        } else {
          if (pitchButtonPressed) {
            let dist = yPercent - bendStartPercents.current[index];
            freq = bendStartFreqs.current[index];
            freq = freq + freq * dist;
          }
          if (spectroContext.state.quantize) {
            heldFreqs.current[index] = freq;
          } else {
            // Ramps to new Frequency and Volume
            synths.current[index].frequency.exponentialRampToValueAtTime(
              freq,
              props.audioContext.currentTime + RAMPVALUE
            );
          }
          // Ramp to new Volume
          synths.current[index].volume.exponentialRampToValueAtTime(
            newGain,
            props.audioContext.currentTime + RAMPVALUE
          );
        }
      }

      //Redraw Labels
      drawPitchBendButton(pitchButtonPressed, e.touches.length);
      for (let i = 0; i < e.touches.length; i++) {
        let pos = getMousePos(canvasRef.current, e.touches[i]);
        let yPercent = 1 - pos.y / spectroContext.state.height;
        let freq = sm_getFreq(yPercent,);
        if (!isPitchButton(pos.x, pos.y)) {
          // this.label(freq, pos.x, pos.y);
          let index = e.touches[i].identifier % NUM_VOICES;
          if (index < 0) index = NUM_VOICES + index;
          drawHarmonics(index, freq, pos.x);
        }
      }
    }
  };

  const onTouchEnd = (e) => {
    e.preventDefault(); // Always need to prevent default browser choices
    if (touch.current && !spectroContext.state.sustain) {
      let { width, height } = spectroContext.state;
      ctx.current.clearRect(0, 0, width, height);
      if (spectroContext.state.noteLinesOn) {
        renderNoteLines();
      }
      // Check if there are more touches changed than on the screen and release everything (mostly as an fail switch)
      // if (true) {
        // if (e.changedTouches.length === e.touches.length + 1) {
        //   Tone.Transport.cancel();
        //   for (let i = 0; i < NUM_VOICES; i++) {
        //     synths.current[i].triggerRelease();
        //     amSignals.current[i].triggerRelease();
        //     fmSignals.current[i].triggerRelease();
        //     this.checkHeldFreq(i);
        //   }
        //   this.drawPitchBendButton(false);
        //   this.setState({touch: false, notAllRelease: false, currentVoice: -1, pitchButtonPressed: false});
        // } else {
        let checkButton = false;
        // Does the same as onTouchMove, except instead of changing the voice, it deletes it.
        for (let i = 0; i < e.changedTouches.length; i++) {
          let pos = getMousePos(canvasRef.current, e.changedTouches[i]);
          let index = e.changedTouches[i].identifier % NUM_VOICES;
          if (index < 0) index = NUM_VOICES + index;

          if (!isPitchButton(pos.x, pos.y)) {
            if (pitchButtonPressed) {
              // Ramps to new Frequency and Volume
              synths.current[index].frequency.exponentialRampToValueAtTime(
                bendStartFreqs.current[index],
                props.audioContext.currentTime + 0.2
              );
              // Ramp to new Volume
              synths.current[index].volume.exponentialRampToValueAtTime(
                bendStartVolumes.current[index],
                props.audioContext.currentTime + 0.05
              );
              bendStartPercents.current[index] = 0;
              bendStartFreqs.current[index] = 0;
              bendStartVolumes.current[index] = 0;
            } else {
              synths.current[index].triggerRelease();
              amSignals.current[index].triggerRelease();
              fmSignals.current[index].triggerRelease();
              checkHeldFreq(index);
              synths.current[index].triggerAttackRelease(synths.current[index].frequency.value, 0.001);
              if (spectroContext.state.quantize) {
                Tone.getTransport().clear(heldIds.current[index]);
              }
            }
            drawPitchBendButton(pitchButtonPressed, e.touches.length);
          } else {
            // If pitch bend button let go, release all if no current touching fingers and
            // set checkButton to true
            if (e.touches.length === 0) {
              for (let i = 0; i < NUM_VOICES; i++) {
                if (synths.current[i].oscillator.state === 'started') {
                  synths.current[i].triggerRelease();
                  fmSignals.current[i].triggerRelease();
                  amSignals.current[i].triggerRelease();
                  checkHeldFreq(i);
                  synths.current[index].triggerAttackRelease(synths.current[index].frequency.value, 0.001);
                }
              }
            }
            setPitchButtonPressed(false);
            drawPitchBendButton(false, e.touches.length);
            checkButton = true;
          }
        }
        // Only reset the index if not pushing pitch bend button
        if (!checkButton) {
          let newVoice = currentVoice - e.changedTouches.length;
          newVoice = newVoice < 0 ? NUM_VOICES + newVoice : newVoice;
          setCurrentVoice(newVoice);
        }
      // }

      //Redraw Labels
      for (let i = 0; i < e.touches.length; i++) {
        let pos = getMousePos(canvasRef.current, e.touches[i]);
        let yPercent = 1 - pos.y / spectroContext.state.height;
        let freq = sm_getFreq(yPercent,);
        if (!isPitchButton(pos.x, pos.y)) {
          let index = e.touches[i].identifier % NUM_VOICES;
          if (index < 0) index = NUM_VOICES + index;
          drawHarmonics(index, freq, pos.x);
          // this.label(freq, pos.x, pos.y);
        }
      }
    }
  };

  const MIDINoteOn = (e) => {
    setAudioVariables();
    let { resolutionMax, resolutionMin, height, width, log } = spectroContext.state;
    ctx.current.clearRect(0, 0, width, height);
    if (spectroContext.state.noteLinesOn) {
      renderNoteLines();
    }
    // For each note, do the same as above in onMouseDown
    let xPercent = 1 - e.velocity;
    let gain = getGain(xPercent);
    let freq = midiToFreq(e.note.number);
    let yPos = freqToIndex(freq, resolutionMax, resolutionMin, height, log);
    let yPercent = 1 - yPos / height;
    let newVoice = (currentVoice + 1) % NUM_VOICES;
    if (spectroContext.state.quantize) {
      let id = Tone.getTransport().scheduleRepeat((time) => {
        synths.current[newVoice].triggerAttackRelease(heldFreqs.current[newVoice], '@8n.'); // Starts the synth at frequency = freq
      }, '4n');
      heldFreqs.current[newVoice] = freq;
      heldIds.current[newVoice] = id;
    } else {
      synths.current[newVoice].triggerAttack(freq);
    }
    // Am
    if (spectroContext.state.amOn) {
      let newVol = convertToLog(spectroContext.state.amLevel, 0, 1, 0.01, 15); // AM amplitud;e set between 0.01 and 15 (arbitray choices)
      let newFreq = convertToLog(spectroContext.state.amRate, 0, 1, 0.5, 50); // AM frequency set between 0.5 and 50 hz (arbitray choices)
      amSignals.current[newVoice].volume.exponentialRampToValueAtTime(
        newVol,
        props.audioContext.currentTime + 1
      ); // Ramps to AM amplitude in 1 sec
      amSignals.current[newVoice].triggerAttack(newFreq);
    }
    // FM
    if (spectroContext.state.fmOn) {
      let modIndex = (1 - freqToIndex(freq, 20000, 20, 1)) * 1.2; // FM index ranges from 0 - 2
      let newVol = convertToLog(spectroContext.state.fmLevel, 0, 1, 40, 100); // FM amplitude set between 30 and 60 (arbitrary choices)
      let newFreq = convertToLog(spectroContext.state.fmRate, 0, 1, 0.5, 50); // FM Frequency set between 0.5 and 50 (arbitray choices)
      // let newFreq = convertToLog(yPercent, 0, 1, 0.5, 20); // FM Frequency set between 0.5 and 20 (arbitray choices)
      fmSignals.current[newVoice].volume.exponentialRampToValueAtTime(
        newVol * modIndex,
        props.audioContext.currentTime + RAMPVALUE
      ); // Ramps to FM amplitude*modIndex in RAMPVALUE sec
      fmSignals.current[newVoice].triggerAttack(newFreq);
    }

    drawPitchBendButton(pitchButtonPressed);
    midiNotes.current[newVoice] = {
      xPercent: xPercent,
      yPercent: yPercent,
      label: e.note.name + (parseInt(e.note.octave, 10) - 1),
      id: newVoice,
    };
    setCurrentVoice(newVoice);
    let max = -Infinity;
    if (spectroContext.state.harmonicsOn) {
      synths.current[newVoice].oscillator.partials = harmonicWeights
        .slice(0, spectroContext.state.numHarmonics + 1)
        .map((weight, index) => {
          let weightFromFilter = getFilterCoeficients(freq * (index + 1));
          if (weightFromFilter > max) {
            max = weightFromFilter;
          }
          return weightFromFilter * weight;
        });
    } else {
      synths.current[newVoice].oscillator.partials = harmonicWeights
        .slice(0, 1)
        .map((weight, index) => {
          let weightFromFilter = getFilterCoeficients(freq * (index + 1));
          if (weightFromFilter > max) {
            max = weightFromFilter;
          }
          return weightFromFilter * weight;
        });
    }
    let newGain = gain + 20 * Math.log10(max);
    synths.current[newVoice].volume.value = newGain;
    if (pitchButtonPressed) {
      bendStartPercents.current[newVoice] = yPercent;
      bendStartFreqs.current[newVoice] = freq;
      bendStartVolumes.current[newVoice] = newGain;
    }
    // Draw each note
    midiNotes.current.forEach((item) => {
      if (item) {
        let pos = {
          x: (1 - item.xPercent) * width,
          y: (1 - item.yPercent) * height,
        };
        // excludeScale is false
        drawHarmonics(newVoice, Math.round(freq), pos.x, false);
        // this.label(item.label, pos.x, pos.y);
      }
    });
  };

  const MIDINoteOff = (e) => {
    let { resolutionMax, resolutionMin, height, width, log } = spectroContext.state;
    ctx.current.clearRect(0, 0, width, height);
    if (spectroContext.state.noteLinesOn) {
      renderNoteLines();
    }
    let freq = midiToFreq(e.note.number);
    let yPos = freqToIndex(freq, resolutionMax, resolutionMin, height, log);
    let yPercent = 1 - yPos / height;
    // Release note at particular index, otherwise redraw other notes
    midiNotes.current.forEach((item, index, arr) => {
      if (item && !isNaN(item.xPercent)) {
        if (item.yPercent === yPercent) {
          synths.current[item.id].triggerRelease();
          checkHeldFreq(item.id);
          arr[index] = {};
        } else {
          let pos = {
            x: (1 - item.xPercent) * width,
            y: (1 - item.yPercent) * height,
          };
          let freq = getFreq(item.yPercent, resolutionMin, resolutionMax, log);
          // excludeScale is false
          drawHarmonics(index, Math.round(freq), pos.x, false);
          label(item.label, pos.x, pos.y, 1);
        }
      }
    });
    drawPitchBendButton(pitchButtonPressed);
  };

  const sustainChangeTimbre = () => {
    let { soundOn, height, width, /*numHarmonics,*/ noteLinesOn } = spectroContext.state;
    if (soundOn) {
      ctx.current.clearRect(0, 0, width, height);
      for (let i = 0; i < NUM_VOICES; i++) {
        let freq = synths.current[i].frequency.value;
        synths.current[i].oscillator.type = 'custom';
        let max = -Infinity;
        if (spectroContext.state.harmonicsOn) {
          synths.current[i].oscillator.partials = harmonicWeights
            .slice(0, spectroContext.state.numHarmonics + 1)
            .map((weight, index) => {
              let weightFromFilter = getFilterCoeficients(freq * (index + 1));
              if (weightFromFilter > max) {
                max = weightFromFilter;
              }
              return weightFromFilter * weight;
            });
        } else {
          synths.current[i].oscillator.partials = harmonicWeights
            .slice(0, 1)
            .map((weight, index) => {
              let weightFromFilter = getFilterCoeficients(freq * (index + 1));
              if (weightFromFilter > max) {
                max = weightFromFilter;
              }
              return weightFromFilter * weight;
            });
        }
        if (synths.current[i].oscillator.state === 'started') {
          let xPos = (1 - gainToLinear(synths.current[i].volume.value)) * width;
          drawHarmonics(i, freq, xPos);
        }
      }
      if (noteLinesOn) {
        renderNoteLines();
      }
      drawPitchBendButton(pitchButtonPressed);
    }
  }

  const getFilterCoeficients = (freq) => {
    let { height, resolutionMax, resolutionMin } = spectroContext.state;
    // Test Function f
    // let f = x => Math.pow(Math.E,(-0.0002*x));
    let y = spectroContext.queryFilter(
      freqToIndex(freq, resolutionMax, resolutionMin, height) / height
    );
    return y;
    // return f(freq);
  }

  const releaseAll = () => {
    for (let i = 0; i < NUM_VOICES; i++) {
      if (synths.current[i].oscillator.state === 'started') {
        synths.current[i].triggerRelease();
        checkHeldFreq(i);
      }
    }
    mouseDown.current = false;
    touch.current = false;
    let { height, width } = spectroContext.state;
    ctx.current.clearRect(0, 0, width, height);
    if (spectroContext.state.noteLinesOn) {
      renderNoteLines();
    }
    drawPitchBendButton(pitchButtonPressed, 0);
  }

  // default parameter as excludeScaleState if excludeScale does not have a value
  const drawHarmonics = (index, fundamental, xPos, excludeScale = excludeScaleState) => {
    let { height, width, resolutionMax, resolutionMin, log } = spectroContext.state;
    if (spectroContext.state.harmonicsOn) {
      for (let i = 0; i < spectroContext.state.numHarmonics + 1; i++) {
        let freq = fundamental * (i + 1);
        let pos = {
          // Not needed anymore, since opacity essentially uses this with slight adjustments
          // x: synths.current[index].oscillator.partials[i] * xPos,
          y: freqToIndex(freq, resolutionMax, resolutionMin, height, log),
        };
        let opacity = synths.current[index].oscillator.partials[i] * ((xPos + 100) / width);
        if (i === 0) {
          label(freq, xPos, pos.y, opacity, excludeScale);
        } else {
          label('', xPos, pos.y, opacity, excludeScale);
        }
      }
    }
    else {
      let freq = fundamental;
      let pos = {
        // Not needed anymore, since opacity essentially uses this with slight adjustments
        // x: synths.current[index].oscillator.partials[i] * xPos,
        y: freqToIndex(freq, resolutionMax, resolutionMin, height, log),
      };
      let opacity = synths.current[index].oscillator.partials[0] * ((xPos + 100) / width);
      label(freq, xPos, pos.y, opacity);
    }
  }

  // Helper function that determines the frequency to play based on the mouse/finger position
  // Also deals with snapping it to a scale if scale mode is on
  // TODO: FIX THE ISSUE WHERE ON MOUSE DOWN, IF RIGHT CLICK OR INSIDE EXCLUDE SCALE AREA ON RIGHT SIDE, STILL SNAPPPING TO SCALE.
  // I BELIEVE IT'S BC STATE IS SET FROM MOUSEDOWN, AND CHECKED IN GETFREQ, BUT THE STATE HASN'T BEEN SET UNTIL AFTER GETFREQ HAS BEEN CALLED
  const sm_getFreq = (index, excludeScale=false) => {
    let { resolutionMax, resolutionMin, log/*, height*/ } = spectroContext.state;
    let freq = getFreq(index, resolutionMin, resolutionMax, log);
    // let notes = [];

    if (spectroContext.state.scaleOn && !excludeScale ) {
    // if (spectroContext.state.scaleOn && !this.state.excludeScale) {
      //  Maps to one of the 12 keys of the piano based on note and accidental
      let newIndexedKey = spectroContext.state.musicKey.value;
      // Edge cases
      if (newIndexedKey === 0 && spectroContext.state.accidental.value === 2) {
        // Cb->B
        newIndexedKey = 11;
      } else if (newIndexedKey === 11 && spectroContext.state.accidental.value === 1) {
        // B#->C
        newIndexedKey = 0;
      } else {
        newIndexedKey =
          spectroContext.state.accidental.value === 1
            ? newIndexedKey + 1
            : spectroContext.state.accidental.value === 2
            ? newIndexedKey - 1
            : newIndexedKey;
      }
      // Uses generateScale helper method to generate base frequency values
      let s = generateScale(
        newIndexedKey,
        spectroContext.state.scale.value,
        spectroContext.state.justIntonation,
        spectroContext.state.customScaleValue
      );
      let name = s.scale[0];
      let note = 0;
      // dist is max freq
      let dist = 20000;
      let harmonic = 0;
      //let finalJ = 0;
      //let finalK = 0;

      // calculate B0
      let context_scale_array = spectroContext.state.scaleNotes;

      // get index of current scale in scale array, and 'B'
      let indexCurrentKey = context_scale_array.indexOf(s.scale[0]);
      let indexB = context_scale_array.indexOf('B');
      
      // Calculate the number of notes between the current key and 'B'
      let intonation_idx_B = (indexB - indexCurrentKey + context_scale_array.length) % context_scale_array.length;
      
      // calculate the frequency for B0 (technically this would be C1, but we want the breakpoint of when the octave # changes)
      // Use generateFullScale to get frequencies of all notes, which includes B0
      let full_s = generateFullScale(newIndexedKey,
        spectroContext.state.justIntonation,
      );
      let B0_Freq = full_s.scale[intonation_idx_B];
      // console.log(B0_Freq)
      
      let B0_Log = Math.log2(B0_Freq);
      //Sweeps through scale object and plays correct frequency
      for (var j = 1; j < 1500; j = j * 2) {
        for (var k = 0; k < s.scale.length; k++) {
          var check = j * s.scale[k];
          var checkDist = Math.abs(freq - check);
          if (checkDist < dist) {
            dist = checkDist;
            note = check;
            name = s.scaleNames[k];
            harmonic = Math.floor((Math.log2(note) - B0_Log).toFixed(8));
            // console.log(Math.log2(note) - B0_Log);
            //finalJ = j;
            //finalK = k;
          } else {
            break;
          }
        }
      }
      freq = note;
      let textLabel = name + '' + (harmonic + 1); // +1 to shift to musical standard
      // console.log((Math.log2(note) - B0_Log).toFixed(8));
      // console.log(harmonic);
      // console.log(textLabel);
      scaleLabel.current = textLabel;
    }
    return Math.round(freq);
  }

  const handleResize = () => {
    props.handleResize();  // this also clears the notelines, then adds them in the above handleresize
    ctx.current.clearRect(0, 0, spectroContext.state.width, spectroContext.state.height);
    if (spectroContext.state.noteLinesOn) {
      renderNoteLines();
    }
    drawPitchBendButton(false);
  };

  // Helper method that generates a label for the frequency or the scale note
  const label = (freq, x, y, opacity, excludeScale) => {
    const offset = 30; //before 25
    const scaleOffset = 10;
    ctx.current.font = '20px Inconsolata';
    ctx.current.fillStyle = 'white';
    if (spectroContext.state.soundOn) {
      if (spectroContext.state.midi || freq === '') {
        ctx.current.fillText(freq, x + offset, y - offset);
      } else if (!spectroContext.state.scaleOn || excludeScaleState) {
        //|| spectroContext.state.harmonicsOn) {
        ctx.current.fillText(freq + ' Hz', x + offset, y - offset);
      } else {
        ctx.current.fillText(scaleLabel.current, x + offset, y - offset);
        let index = freqToIndex(
          freq,
          spectroContext.state.resolutionMax,
          spectroContext.state.resolutionMin,
          spectroContext.state.height,
          spectroContext.state.log
        );
        // Scale Mode Label at side
        let width = (freq + ' Hz').length < 7 ? 70 : 80;
        ctx.current.fillStyle = 'rgba(218, 218, 218, 0.8)';
        ctx.current.fillRect(scaleOffset - 2, index - 2 * scaleOffset, width, 3.5 * scaleOffset);
        ctx.current.fillStyle = 'white';
        ctx.current.fillText(freq + ' Hz', scaleOffset + width / 2, index + scaleOffset / 2);
      }
      // Draw Circle for point
      const startingAngle = 0;
      const endingAngle = 2 * Math.PI;
      const radius = 10;
      const color = 'rgba(255, 255, 0, ' + opacity + ')';
      const outline = 'rgba(0, 0, 0, ' + opacity + ')';
      ctx.current.beginPath();
      ctx.current.ellipse(x, y, radius, radius / 2, 0, startingAngle, endingAngle);
      ctx.current.fillStyle = color;
      ctx.current.fill();
      ctx.current.strokeStyle = outline;
      ctx.current.stroke();
    }
  }

  const drawPitchBendButton = (buttonClicked, numTouch) => {
    const x = 10;
    const y = spectroContext.state.height - spectroContext.state.height * 0.12;
    const width = spectroContext.state.width * 0.05;
    const height = width;
    const arcsize = 25;

    if (numTouch > 0) {
      if (buttonClicked) {
        ctx.current.fillStyle = '#1c9fdb';
      } else {
        ctx.current.fillStyle = '#56caff';
      }
      // console.log("draw")
      ctx.current.save();

      ctx.current.beginPath();
      ctx.current.moveTo(x, y);
      ctx.current.moveTo(x + arcsize, y);
      ctx.current.lineTo(x + width - arcsize, y);
      ctx.current.arcTo(x + width, y, x + width, y + arcsize, arcsize);
      ctx.current.lineTo(x + width, y + height - arcsize);
      ctx.current.arcTo(x + width, y + height, x + width - arcsize, y + height, arcsize);
      ctx.current.lineTo(x + arcsize, y + height);
      ctx.current.arcTo(x, y + height, x, y - arcsize, arcsize);
      ctx.current.lineTo(x, y + arcsize);
      ctx.current.arcTo(x, y, x + arcsize, y, arcsize);
      ctx.current.lineTo(x + arcsize, y);
      ctx.current.stroke();
      ctx.current.fill();
      ctx.current.fillStyle = 'white';
      ctx.current.font = '20px Inconsolata';
      ctx.current.textAlign = 'center';
      ctx.current.fillText('Bend', x + width / 2, y + height / 2 + 5);
    }
    else {
      // console.log("clear")
      ctx.current.restore();
    }
  }

  const isPitchButton = (x, y) => {
    let { height, width } = spectroContext.state;
    let condition1 = x >= 0 && x <= width * 0.05 + 30;
    let condition2 = y >= height - height * 0.12 && y <= width * 0.05 + height - height * 0.12;
    if (condition1 && condition2) {
      return true;
    }
    return false;
  }

  const renderNoteLines = () => {
    const NUM_OCTAVES_TO_DISPLAY_NOTELINE_NAMES = 3.5   // number of octaves at which noteline names will begin appearing. Added a slight buffer, gives a couple more note lines to appear on the edges
    let { height, width, resolutionMax, resolutionMin, log } = spectroContext.state;
    //  Maps to one of the 12 keys of the piano based on note and accidental
    let newIndexedKey = spectroContext.state.musicKey.value;
    // Edge cases
    if (newIndexedKey === 0 && spectroContext.state.accidental.value === 2) {
      // Cb->B
      newIndexedKey = 11;
    } else if (newIndexedKey === 11 && spectroContext.state.accidental.value === 1) {
      // B#->C
      newIndexedKey = 0;
    } else {
      newIndexedKey =
        spectroContext.state.accidental.value === 1
          ? newIndexedKey + 1
          : spectroContext.state.accidental.value === 2
          ? newIndexedKey - 1
          : newIndexedKey;
    }

    frequencies.current = {};
    // Uses generateScale helper method to generate base frequency values

    let s = generateScale(
      newIndexedKey,
      spectroContext.state.scale.value,
      spectroContext.state.justIntonation,
      spectroContext.state.customScaleValue
    );
    
    // calculate B0
    // to know what the notes are. We can technically use any array containing all named notes in a scale; it does not necessarily need to be the one stored in context
    let context_scale_array = spectroContext.state.scaleNotes;

    // get index of current scale in scale array, and 'B'
    let indexCurrentKey = context_scale_array.indexOf(s.scale[0]);
    let indexB = context_scale_array.indexOf('B');
    
    // Calculate the number of notes between the current key and 'B'
    let intonation_idx_B = (indexB - indexCurrentKey + context_scale_array.length) % context_scale_array.length;
    
    // calculate the frequency for B0 (technically this would be C1, but we want the breakpoint of when the octave # changes)
    // Use generateFullScale to get frequencies of all notes, which includes B0
    let full_s = generateFullScale(newIndexedKey,
      spectroContext.state.justIntonation,
    );
    let B0_Freq = full_s.scale[intonation_idx_B];
    let B0_Log = Math.log2(B0_Freq);

    // loop over octaves of the scale (not universal c0 scale btw)
    for (let j = 1; j < 1500; j = j * 2) {
    //Sweeps through scale object and draws frequency
      for (let i = 0; i < s.scale.length; i++) {
        let freq = j * s.scale[i];

        if (freq > resolutionMax) {
          break;
        } else {
          let harmonic = Math.floor((Math.log2(freq) - B0_Log).toFixed(8));   // get the correct octave according to octave of C0 universal scale
          let label = s.scaleNames[i] + '' + (harmonic + 1);
          let index = freqToIndex(freq, resolutionMax, resolutionMin, height, log);

          frequencies.current[label] = freq;

          // if(this.goldIndices.includes(index) && spectroContext.state.soundOn){
          //   ctx.current.fillStyle = 'gold';
          // } else
          if (s.scalePattern[i] === 0) {
            ctx.current.fillStyle = 'rgba(171,226,251, 0.8)'; // blue line for key note
          } else {
            ctx.current.fillStyle = 'rgba(255, 255, 255, 0.5)'; // white line for other notes
          }
          
          ctx.current.fillRect(0, index, width - NOTE_LINE_WIDTH, 1); // x, y, width,height
          if (resolutionMin * 2**NUM_OCTAVES_TO_DISPLAY_NOTELINE_NAMES >= resolutionMax ) {
            ctx.current.textAlign = "right"
            ctx.current.fillText(label, spectroContext.state.width - NOTE_LINE_WIDTH, index - 4); // place text slightly above noteline
            ctx.current.textAlign = "center" // reset ctx textalign... prob not needed
          }
        }
      }
    }
  }

  const removeNoteLines = () => {
    ctx.current.clearRect(0, 0, spectroContext.state.width, spectroContext.state.height);
  }

  // useImperativeHandle so that the parent can access these methods using the correct ref
  useImperativeHandle(ref, () => ({
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    MIDINoteOn,
    MIDINoteOff,
    sustainChangeTimbre,
    releaseAll,
    removeNoteLines,
    renderNoteLines,
    drawPitchBendButton,
  }));

  return (
    <canvas
      className="osc-canvas"
      style={{ pointerEvents: props.disableInteraction ? 'none' : 'auto' }}
      onContextMenu={(e) => e.preventDefault()}
      onMouseDown={(e) => onMouseDown(e)}
      onMouseUp={(e) => onMouseUp(e)}
      onMouseMove={(e) => onMouseMove(e)}
      onMouseOut={(e) => onMouseOut(e)}
      onTouchStart={(e) => onTouchStart(e)}
      onTouchEnd={(e) => onTouchEnd(e)}
      onTouchMove={(e) => onTouchMove(e)}
      width={spectroContext.state.width}
      height={spectroContext.state.height}
      ref={canvasRef}
    />
  );
});

SoundMaking.displayName = 'SoundMaking';
// SoundMaking.contextType = SpectrogramContext;
export default SoundMaking;
